import { directionLabel } from "../../modules/Reports/Camera/CameraDirections";
import { resolveLastUpdated, describeAge } from "../../util/CameraAgeUtil";

/**
 * Builds the camera popup shown in the route planner's Google Maps InfoWindow.
 *
 * This is plain HTML rather than React because InfoWindow.setContent takes markup, so the
 * direction buttons carry inline handlers - the same way the image's open-in-a-window click
 * already worked. The handlers are written against `this` and the button's own parent, so
 * nothing here reaches outside the popup's markup or into global scope.
 *
 * A multi-direction camera sends one URL per direction in `remUrls`, with `dirs[i]` naming
 * `remUrls[i]`. Buttons are only drawn when those two agree in length: a server that sends a
 * short or empty `dirs` would otherwise label the images with the wrong directions, which is
 * worse than the single image this used to show.
 */

let popupSequence = 0;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// A literal backslash, spelled this way so the escaping below reads as what it does.
const BACKSLASH = String.fromCharCode(92);

/**
 * Escapes a value for a single-quoted JavaScript string inside an inline handler.
 *
 * The quotes around it are written as &#39; so the HTML parser hands the JS its own quotes.
 * A quote inside the value therefore has to be escaped for JavaScript, not for HTML - an
 * &#39; would decode to a bare quote and end the string early.
 */
const escapeJs = (value) =>
  String(value ?? "")
    .split(BACKSLASH)
    .join(BACKSLASH + BACKSLASH)
    .split("'")
    .join(BACKSLASH + "&#39;");

const BUTTON_STYLE =
  "border:1px solid #cbd5e1;border-radius:6px;padding:2px 8px;margin:0 2px;" +
  "font-size:11px;line-height:1.6;cursor:pointer;background:#fff;color:#1f2937;";

const ACTIVE_BACKGROUND = "#dbeafe";

/** Point the image at this direction and move the highlight onto this button. */
const switchDirectionScript = (imageId, url) =>
  "var b=this.parentNode.children;" +
  "for(var i=0;i&lt;b.length;i++){b[i].style.background=&#39;#fff&#39;;b[i].style.fontWeight=&#39;normal&#39;;}" +
  "this.style.background=&#39;" +
  ACTIVE_BACKGROUND +
  "&#39;;this.style.fontWeight=&#39;bold&#39;;" +
  "document.getElementById(&#39;" +
  imageId +
  "&#39;).src=&#39;" +
  escapeJs(url) +
  "&#39;;";

export const buildCameraPopupHtml = (props) => {
  const encodedId = encodeURIComponent(props.id);
  const remUrls = props.remUrls || [];
  const dirs = props.dirs || [];
  const isRemote = remUrls.length > 0;
  const imgSrc = isRemote ? remUrls[0] : `/lmiga/snapshot?id=${encodedId}`;
  const imageId = `tm-route-camera-img-${(popupSequence += 1)}`;

  const referrerPolicy =
    isRemote && imgSrc.includes("cctv.travelmidwest.com")
      ? "origin"
      : isRemote
        ? "no-referrer"
        : "";
  const referrerAttr = referrerPolicy
    ? ` referrerpolicy="${referrerPolicy}"`
    : "";

  // Open whichever direction is on screen, not whichever one the popup opened on.
  const enlarge =
    "window.open(document.getElementById(&#39;" +
    imageId +
    "&#39;).src," +
    "&#39;_blank&#39;,&#39;width=800,height=600&#39;)";

  const showButtons = remUrls.length > 1 && dirs.length === remUrls.length;
  const buttons = showButtons
    ? `<div style="text-align:center;margin-top:4px;">` +
      remUrls
        .map((url, i) => {
          const label = directionLabel(dirs[i]) || dirs[i];
          const style =
            i === 0
              ? `${BUTTON_STYLE}background:${ACTIVE_BACKGROUND};font-weight:bold;`
              : BUTTON_STYLE;
          return (
            `<button type="button" style="${style}" ` +
            `onclick="${switchDirectionScript(imageId, url)}">${escapeHtml(label)}</button>`
          );
        })
        .join("") +
      `</div>`
    : dirs.length > 1
      ? `<div style="text-align:center;margin-top:2px;font-size:11px;">${escapeHtml(dirs[0] || "")}</div>`
      : "";

  return (
    `<div style="max-width:320px;font-size:13px;color:#1f2937;">` +
    `<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(props.locDesc || "Camera")} (${escapeHtml(props.src || "")})</div>` +
    `<div style="margin-bottom:4px;">Last Updated: ${escapeHtml(describeAge(resolveLastUpdated(props)).text)}</div>` +
    `<img id="${imageId}" src="${escapeHtml(imgSrc)}" alt="Camera image"` +
    ` style="width:100%;max-height:200px;object-fit:contain;cursor:pointer;"${referrerAttr}` +
    ` onclick="${enlarge}" />` +
    buttons +
    `</div>`
  );
};

export default buildCameraPopupHtml;
